/**
 * `<VirtualizedResultList>` — the windowed search-result list (frontend.md §2.1, §1.5).
 *
 * Wraps the shared `<VirtualizedList>` with `<ResultCard>` rows. At 100K notes (D-009) the
 * result set is never rendered whole — the list windows the visible rows and pulls the
 * next cursor page via `onEndReached` (frontend.md §1.5). Surfaces the `truncated` flag
 * and any `meta.warnings` (e.g. "rerank skipped: lean tier") so a degradation is never
 * hidden (frontend.md §4.2 / conventions.md §3).
 *
 * U7 (design review 2026-07-05 P1 item 1) — weak-matches fold: the surface
 * partitions results by the relevance floor (`resultRelevance.ts`) and passes
 * the below-floor tail as `weakItems`. They render BEHIND a collapsed
 * disclosure row inside the same virtualized window, so deep-rank stragglers
 * never read as peers of strong hits. The fold state is component-local and
 * resets with the list (the surface keys this component by query+mode).
 */

import { useState } from 'react';
import type { ResultItem } from '@/types';
import { VirtualizedList, LoadingState, Icon } from '@/components';
import { ResultCard } from './ResultCard';
import type { ResultDensity } from './searchUrlState';

/** One virtualized row — a result, the weak-fold disclosure, or a folded weak result. */
type ResultRow =
  | { kind: 'result'; item: ResultItem }
  | { kind: 'weak-fold'; count: number; open: boolean }
  | { kind: 'weak-result'; item: ResultItem };

interface VirtualizedResultListProps {
  items: ResultItem[];
  /** Below-relevance-floor results, folded under the disclosure row. */
  weakItems?: ResultItem[];
  truncated: boolean;
  warnings: string[];
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onOpenDetail: (item: ResultItem) => void;
  /** Visual density — defaults to `cards`. Affects per-row size estimate. */
  density?: ResultDensity;
}

export function VirtualizedResultList({
  items,
  weakItems = [],
  truncated,
  warnings,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  onOpenDetail,
  density = 'cards',
}: VirtualizedResultListProps): JSX.Element {
  const [weakOpen, setWeakOpen] = useState(false);

  // Per-row height estimate — compact rows are roughly half the height of cards rows.
  // The virtualizer dynamically measures; this is just the initial budget.
  const estimateSize = density === 'compact' ? 44 : 120;

  // Row model: strong results, then (when a weak tail exists) the disclosure
  // row, then — only while open — the weak results themselves.
  const rows: ResultRow[] = items.map((item) => ({ kind: 'result', item }));
  if (weakItems.length > 0) {
    rows.push({ kind: 'weak-fold', count: weakItems.length, open: weakOpen });
    if (weakOpen) {
      for (const item of weakItems) rows.push({ kind: 'weak-result', item });
    }
  }

  return (
    <div className="virtualized-result-list">
      {warnings.length > 0 && (
        <div className="warnings-banner" role="status">
          {warnings.join(' · ')}
        </div>
      )}
      {truncated && (
        <div className="warnings-banner" role="status">
          Results were truncated by a hard cap — narrow your filters or paginate further.
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <VirtualizedList
          items={rows}
          getKey={(row) => (row.kind === 'weak-fold' ? '__weak-fold__' : row.item.noteId)}
          estimateSize={estimateSize}
          ariaLabel="Search results"
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) onLoadMore();
          }}
          renderRow={(row) => {
            if (row.kind === 'weak-fold') {
              return (
                <button
                  type="button"
                  className="se-weak-fold"
                  aria-expanded={row.open}
                  onClick={() => setWeakOpen((v) => !v)}
                  title="Scores well below the best hit (relevance floor) — folded so marginal overlaps don't read as peers of strong results"
                >
                  <Icon name={row.open ? 'chevron_down' : 'chevron_right'} size={12} />
                  <span>
                    {row.count} weak match{row.count === 1 ? '' : 'es'}
                  </span>
                  <span className="se-weak-fold__hint">below relevance floor</span>
                </button>
              );
            }
            const card = (
              <ResultCard item={row.item} onOpenDetail={onOpenDetail} density={density} />
            );
            return row.kind === 'weak-result' ? (
              <div className="se-weak-row">{card}</div>
            ) : (
              card
            );
          }}
        />
      </div>
      {isFetchingNextPage && <LoadingState variant="inline" label="Loading more results…" />}
    </div>
  );
}
